import { useRoutesContext } from '@src/app/context/Routes/Routes';
import { useSave } from '@src/entities/ActionPath/ui/hooks/useSave';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useAssets } from '@src/shared/ui/hooks/useAssets';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { isEmpty, isEqual, isNil, isUndefined } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import { Layout } from './components/misc/Layout';
import { useWizardContext } from './context';
import { useSelectedCommonCriteria } from './hooks/useSelectedCommonCriteria';
import { isValidStep } from './includes/functions';

/**
 * Wizard
 *
 * This is step-by-step component to compose an "action path". Supports both creation and modification of an action
 * path.
 *
 * @returns {Element}
 * @constructor
 */
export function Wizard() {
  const router = useRouter();

  const [previouslySubmittedValues, setPreviouslySubmittedValues] = useState({});

  const { handleSelected: setSelectedCommonEntrance, selected: selectedCommonEntrance } =
    useSelectedCommonCriteria('entrance');
  const { handleSelected: setSelectedCommonExit, selected: selectedCommonExit } = useSelectedCommonCriteria('exit');

  const { action, actionPath, currentStep, mode, nextStep } = useWizardContext();
  const { currentRoute } = useRoutesContext();
  const { assets, isLoadingAssets } = useAssets([
    {
      apiKey: 'Action',
      queryKey: 'ListActions',
    },
    {
      apiKey: 'Default',
      queryKey: 'ListChronoUnits',
    },
    {
      apiKey: 'RiskRecord',
      queryKey: 'ListRiskCodes',
    },
    {
      apiKey: 'Source',
      queryKey: 'ListSources',
    },
    {
      apiKey: 'Initiative',
      queryKey: 'ListInitiativeTypes',
    },
  ]);
  const { handleSave: handleCreate, saveEvent: createEvent } = useSave('create');
  const { handleSave: handleUpdate, saveEvent: updateEvent } = useSave('update');

  const isSaving = createEvent?.isPending || updateEvent?.isPending;

  const defaultValues = {
    criteria: {
      entrance: {
        additional: [{ column: '', condition: '', value: [] }],
        common: [],
        commonCriteriaVersionsIds: [],
        commonIds: [],
        merged: [],
      },
      exit: {
        additional: [{ column: '', condition: '', value: [] }],
        common: [],
        commonCriteriaVersionsIds: [],
        commonIds: [],
        merged: [],
      },
    },
    exitCriteriaMethod: 'DOES_NOT_MEET_ENTRANCE_CRITERIA',
    id: '',
    manualAssociation: mode === 'manual',
    name: '',
    remediation: {
      actionPathScopeCode: 'CURRENT_FUTURE',
      assignments: [
        {
          actionId: '',
          actionName: '',
          impactedSource: '',
          retryAttempts: '',
          retryInterval: '',
          retryIntervalUnit: '',
        },
      ],
      initiativeType: '',
      notes: '',
      remediationMethod: 'OTHER',
    },
    status: 'DRAFT',
  };

  const form = useForm({ defaultValues });

  const { isDirty, isSubmitting, isValid } = form.formState;

  const formValues = form?.getValues();

  /**
   * Updates the form values with API data only if the form is in its default state.
   */
  useEffect(() => {
    if (action === 'edit' && !isEmpty(actionPath) && defaultValues?.id === formValues?.id) {
      const data = defaultValues;

      data.id = actionPath?.id;
      data.name = actionPath?.name;
      data.status = actionPath?.status;

      data.remediation.actionPathScopeCode = actionPath?.actionPathScopeCode;
      data.remediation.remediationMethod = actionPath?.remediationMethod;
      data.remediation.initiativeType = actionPath?.initiativeType;
      data.remediation.notes = actionPath?.note || '';
      data.criteria = actionPath?.criteria;
      data.exitCriteriaMethod = actionPath?.exitCriteriaMethod;
      data.manualAssociation = actionPath?.manualAssociation;
      data.remediation.assignments = actionPath?.assignments;

      form?.reset(data, {
        keepDirty: false,
        keepDirtyValues: false,
        keepErrors: false,
        keepIsValid: false,
        keepSubmitCount: true,
        keepTouched: false,
        keepValues: false,
      });
    }
  }, [action, actionPath, formValues]);

  /**
   * Extract filters from the URL set by the Risk Analysis screen and apply them to the form values.
   */
  useEffect(() => {
    if (action === 'create' && mode === 'automatic' && isEmpty(actionPath) && !isNil(currentRoute?.params?.key)) {
      const decodedKey = Buffer.from(currentRoute?.params?.key, 'base64').toString('utf-8');
      const riskAnalysisState = JSON.parse(decodedKey);

      router?.navigate('/action-paths/0/create/automatic/draft/step/1');

      const data = {
        ...defaultValues,
        criteria: riskAnalysisState?.criteria,
      };

      form?.reset(data, {
        keepDirty: false,
        keepDirtyValues: false,
        keepErrors: false,
        keepIsValid: false,
        keepSubmitCount: true,
        keepTouched: false,
        keepValues: false,
      });

      form?.validate(
        'name',
        () => {},
        () => {}
      );
    }
  }, [action, actionPath, currentRoute?.params?.key, mode]);

  /**
   * handleSubmit
   *
   * Handles form submission and stores the submitted values to prevent duplicate API calls.
   *
   * @type {(function(*): Promise<void>)|*}
   */
  const handleSubmit = useCallback(
    async (submittedValues) => {
      if (isEqual(previouslySubmittedValues, submittedValues)) {
        return;
      }

      setPreviouslySubmittedValues(submittedValues);

      if (!isSaving && isValid && !isSubmitting && isDirty) {
        const payload = { ...submittedValues };

        let response = {};

        if (action === 'edit') {
          payload.manualAssociation = actionPath?.manualAssociation;
          response = await handleUpdate(payload);
        }

        if (action === 'create') {
          response = await handleCreate(payload);
        }

        if (!isEmpty(response)) {
          const data = {
            ...response,
            ...{
              remediation: {
                actionPathScopeCode: response?.actionPathScopeCode,
                assignments: response?.assignments,
                initiativeType: response?.initiativeType,
                notes: response?.note || '',
                remediationMethod: response?.remediationMethod,
              },
            },
          };

          form?.reset(data, {
            keepDirty: false,
            keepDirtyValues: false,
            keepErrors: false,
            keepIsValid: false,
            keepSubmitCount: true,
            keepTouched: false,
            keepValues: false,
          });
        }

        if (action === 'create') {
          form?.reset(defaultValues, {
            keepDirty: false,
            keepDirtyValues: false,
            keepErrors: false,
            keepIsValid: false,
            keepSubmitCount: true,
            keepTouched: false,
            keepValues: false,
          });

          router?.navigate(`/action-paths/${response?.id}/edit/${mode}/draft/step/${nextStep?.order}`);
        }
      }
    },
    [action, actionPath, isDirty, isSaving, isSubmitting, isValid, mode, nextStep]
  );

  const CurrentStep = currentStep?.component;

  const validName = !!isEmpty(form?.formState?.errors?.name);
  const isCurrentStepValid = isValidStep(
    currentStep,
    validName,
    form?.formState?.errors,
    formValues,
    mode,
    selectedCommonEntrance,
    selectedCommonExit
  );

  useEffect(() => {
    form?.setValue('criteria.entrance.commonIds', selectedCommonEntrance?.commonIds, { shouldDirty: true });
    form?.setValue('criteria.entrance.commonCriteriaVersionsIds', selectedCommonEntrance?.commonCriteriaVersionsIds, {
      shouldDirty: true,
    });
    form?.setValue('criteria.exit.commonIds', selectedCommonExit?.commonIds, { shouldDirty: true });
    form?.setValue('criteria.exit.commonCriteriaVersionsIds', selectedCommonExit?.commonCriteriaVersionsIds, {
      shouldDirty: true,
    });
  }, [selectedCommonEntrance, selectedCommonExit]);

  return (
    <ErrorHandler location="src/features/ActionPaths/Wizard/Wizard.jsx">
      <FormProvider autoComplete="off" highlighted state={form}>
        <Layout
          defaultValues={defaultValues}
          form={form}
          handleSubmit={handleSubmit}
          isCurrentStepValid={isCurrentStepValid}
          isLoading={isLoadingAssets}
        >
          {!isUndefined(CurrentStep) && (
            <CurrentStep
              assets={assets}
              defaultValues={defaultValues}
              form={form}
              isLoading={isSaving}
              previouslySubmittedValues={previouslySubmittedValues}
              setSelectedCommonEntrance={setSelectedCommonEntrance}
              setSelectedCommonExit={setSelectedCommonExit}
            />
          )}
        </Layout>
      </FormProvider>
    </ErrorHandler>
  );
}
